/* DEC-20260811-bpc-teaser-placement: non-replacing adapter for one existing Omnisend teaser. */
(() => {
  'use strict';

  const pagePath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (pagePath === '/pages/lgg-first-order') {
    document.documentElement.classList.add('wcs-hide-bpc-teaser-first-order');
    return;
  }

  const ROOT_SELECTOR = '.omnisend-form-66b06fcfd806e233865f09b8-rectangle-teaser';
  const OFFER_COPY_PATTERN = /\b10%\s*OFF\b/i;
  const MOBILE_MAX_WIDTH = 600;
  const MOBILE_FULL_HEIGHT = 180;
  const DESKTOP_FULL_HEIGHT = 180;
  const COMPACT_HEIGHT = 180;
  const MIN_SAFE_HEIGHT = 220;
  const GAP = 16;
  let frame = 0;
  let teaserPresent = false;

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const isRendered = (element) => {
    if (!element) return false;
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0;
  };
  const setAttribute = (element, name, value) => {
    if (value === null) {
      if (element.hasAttribute(name)) element.removeAttribute(name);
    } else if (element.getAttribute(name) !== value) {
      element.setAttribute(name, value);
    }
  };
  const setProperty = (element, name, value) => {
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
  };

  function findCopy(root) {
    const primaryButton = root.querySelector('#omnisend-form-66b06fcfd806e233865f09b8-teaser-btn');
    if (primaryButton && OFFER_COPY_PATTERN.test(normalize(primaryButton.textContent))) return primaryButton;

    return [...root.querySelectorAll('*')]
      .filter((element) => {
        const text = normalize(element.textContent);
        if (!OFFER_COPY_PATTERN.test(text)) return false;
        if (element.matches('button, [role="button"], input, textarea, select')) return false;
        return ![...element.children].some((child) => OFFER_COPY_PATTERN.test(normalize(child.textContent)));
      })
      .sort((a, b) => normalize(a.textContent).length - normalize(b.textContent).length)[0] || null;
  }

  function findClose(root, copyElement) {
    const controls = [...root.querySelectorAll('button, [role="button"]')].filter((element) => element !== copyElement && !element.contains(copyElement));
    return controls.find((element) => /close|dismiss|cancel/i.test([
      element.getAttribute('aria-label'), element.getAttribute('title'), element.getAttribute('data-testid'), element.className
    ].filter(Boolean).join(' '))) || controls.find((element) => /^[×✕✖x]$/i.test(normalize(element.textContent))) || null;
  }

  function visibleHeaderBottom() {
    const header = document.querySelector('.section-header');
    if (!isRendered(header)) return 0;
    const rect = header.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < innerHeight ? Math.max(0, Math.min(innerHeight, rect.bottom)) : 0;
  }

  function visibleStickyTop() {
    const sticky = document.querySelector('#lggStickyBar');
    if (!isRendered(sticky) || sticky.hasAttribute('data-hidden') || sticky.getAttribute('aria-hidden') === 'true') return innerHeight;
    const rect = sticky.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < innerHeight ? Math.max(0, rect.top) : innerHeight;
  }

  function blockingLayerOpen() {
    return Boolean(
      document.querySelector('cart-drawer.active, .drawer.active') ||
      document.querySelector('.lgg-coa:not([hidden]), .modal-video[open], dialog[open]')
    );
  }

  function position(root) {
    const safeTop = Math.max(8, visibleHeaderBottom() + GAP);
    const safeBottom = Math.min(innerHeight - 8, visibleStickyTop() - GAP);
    const available = Math.max(0, safeBottom - safeTop);
    setAttribute(root, 'data-wcs-pill-suspended', blockingLayerOpen() ? '' : null);

    if (available < MIN_SAFE_HEIGHT) {
      setAttribute(root, 'data-wcs-pill-hidden', '');
      return;
    }

    setAttribute(root, 'data-wcs-pill-hidden', null);
    const fullHeight = innerWidth <= MOBILE_MAX_WIDTH ? MOBILE_FULL_HEIGHT : DESKTOP_FULL_HEIGHT;
    const compact = available < fullHeight;
    const height = compact ? Math.min(COMPACT_HEIGHT, available) : fullHeight;
    const idealTop = (innerHeight - height) / 2;
    const top = Math.max(safeTop, Math.min(idealTop, safeBottom - height));
    setAttribute(root, 'data-wcs-pill-mode', compact ? 'compact' : 'normal');
    setProperty(root, '--wcs-bpc-pill-height', `${Math.round(height * 100) / 100}px`);
    setProperty(root, '--wcs-bpc-pill-top', `${Math.round(top * 100) / 100}px`);
  }

  function adapt(root) {
    if (!(root instanceof Element) || !root.matches(ROOT_SELECTOR)) return;
    root.classList.add('wcs-bpc-teaser-pill');
    const copy = findCopy(root);
    if (!copy) {
      setAttribute(root, 'data-wcs-pill-hidden', '');
      return;
    }
    copy.classList.add('wcs-bpc-teaser-pill__copy');
    const close = findClose(root, copy);
    if (close) {
      close.classList.add('wcs-bpc-teaser-pill__close');
      if (!close.getAttribute('aria-label') && !close.getAttribute('title')) close.setAttribute('aria-label', 'Close discount teaser');
    }
    setAttribute(root, 'data-wcs-pill-hidden', null);
    position(root);
  }

  function update() {
    frame = 0;
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    teaserPresent = roots.length > 0;
    roots.forEach(adapt);
  }

  function queueUpdate() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  const start = () => {
    update();

    // Omnisend inserts the teaser asynchronously. Watch only structural changes
    // globally; observing every attribute mutation caused repeated full-document
    // queries while Shopify apps initialized.
    new MutationObserver((mutations) => {
      const teaserChanged = mutations.some((mutation) => {
        if (mutation.target instanceof Element && mutation.target.closest(ROOT_SELECTOR)) return true;
        return [...mutation.addedNodes].some((node) =>
          node instanceof Element && (node.matches(ROOT_SELECTOR) || node.querySelector(ROOT_SELECTOR))
        );
      });
      if (teaserChanged) queueUpdate();
    }).observe(document.documentElement, { childList: true, subtree: true });

    // State changes that can obstruct the pill are user-driven. Reposition after
    // those interactions without subscribing to all app attribute churn.
    const queueIfPresent = () => { if (teaserPresent) queueUpdate(); };
    document.addEventListener('click', queueIfPresent, { passive: true });
    document.addEventListener('transitionend', (event) => {
      if (event.target instanceof Element && event.target.matches('.section-header, #lggStickyBar, cart-drawer, .drawer, .lgg-coa, .modal-video, dialog')) queueUpdate();
    }, { passive: true });
    document.addEventListener('shopify:section:load', queueUpdate);
    addEventListener('resize', queueIfPresent, { passive: true });
    addEventListener('orientationchange', queueIfPresent, { passive: true });
    addEventListener('scroll', queueIfPresent, { passive: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
